import React, { useState, useContext, createContext } from 'react';
import { FaShoppingCart, FaTimes, FaMinus, FaPlus, FaTrash, FaCreditCard } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { notifyNewOrder } from '../utils/telegram';
import { createPayment, isPaymentEnabled } from '../utils/payment';

// Контекст корзины
const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const addToCart = (product) => {
    if (product.stock === 0) {
      alert('Товар отсутствует в наличии');
      return;
    }
    setCartItems(prev => {
      const existingItem = prev.find(item => item.id === product.id);
      if (existingItem) {
        if (existingItem.quantity >= product.stock) {
          alert(`Максимальное количество: ${product.stock} шт.`);
          return prev;
        }
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      const price = parseInt(item.price?.replace('₽', '') || '0');
      return total + (price * item.quantity);
    }, 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      getTotalPrice,
      getTotalItems,
      clearCart,
      isCartOpen,
      setIsCartOpen
    }}>
      {children}
    </CartContext.Provider>
  );
};

const Cart = () => {
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    getTotalPrice,
    isCartOpen,
    setIsCartOpen,
    clearCart
  } = useCart();
  
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    comment: ''
  });

  if (!isCartOpen) return null;

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) return;
    
    try {
      const order = {
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer: customerData,
        totalPrice: getTotalPrice(),
        totalItems: cartItems.reduce((sum, item) => sum + item.quantity, 0),
        status: 'new',
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'orders'), order);
      notifyNewOrder(order);
      
      alert('Заказ оформлен! Мы свяжемся с вами в ближайшее время.');
      
      clearCart();
      setShowCheckoutForm(false);
      setCustomerData({ name: '', phone: '', email: '', address: '', comment: '' });
      setIsCartOpen(false);
    } catch (error) {
      console.error('Ошибка оформления заказа:', error);
      alert('Ошибка оформления заказа. Попробуйте еще раз.');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) return;
    
    try {
      const order = {
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer: customerData,
        totalPrice: getTotalPrice(),
        totalItems: cartItems.reduce((sum, item) => sum + item.quantity, 0),
        status: 'pending_payment',
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'orders'), order);
      
      const paymentData = {
        ...order,
        orderId: docRef.id
      };
      
      createPayment(paymentData);
    } catch (error) {
      console.error('Ошибка:', error);
      alert('Ошибка при оформлении. Попробуйте еще раз.');
    }
  };

  return (
    <div className="cart-overlay" onClick={() => setIsCartOpen(false)}>
      <div className="cart-sidebar" onClick={e => e.stopPropagation()}>
        <div className="cart-header">
          <h3><FaShoppingCart /> Корзина ({cartItems.length})</h3>
          <button 
            className="cart-close"
            onClick={() => setIsCartOpen(false)}
          >
            <FaTimes />
          </button>
        </div>

        <div className="cart-content">
          {cartItems.length === 0 ? (
            <div className="cart-empty">
              <p>Корзина пуста</p>
              <p>Добавьте товары из каталога</p>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-info">
                      <h4>{item.name}</h4>
                      <p className="cart-item-price">{item.price}</p>
                    </div>
                    <div className="cart-item-controls">
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="quantity-btn"
                      >
                        <FaMinus />
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="quantity-btn"
                      >
                        <FaPlus />
                      </button>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="remove-btn"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-footer">
                <div className="cart-total">
                  <strong>Итого: {getTotalPrice()}₽</strong>
                </div>
                
                {!showCheckoutForm ? (
                  <div className="cart-actions">
                    <button 
                      onClick={clearCart}
                      className="clear-cart-btn"
                    >
                      Очистить корзину
                    </button>
                    <button 
                      onClick={() => setShowCheckoutForm(true)}
                      className="checkout-btn"
                    >
                      Оформить заказ
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleCheckout} className="checkout-form">
                    <h4>Контактные данные</h4>
                    <input
                      type="text"
                      placeholder="Имя *"
                      value={customerData.name}
                      onChange={(e) => setCustomerData({...customerData, name: e.target.value})}
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Телефон *"
                      value={customerData.phone}
                      onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                      required
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={customerData.email}
                      onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                    />
                    <textarea
                      placeholder="Адрес доставки *"
                      value={customerData.address}
                      onChange={(e) => setCustomerData({...customerData, address: e.target.value})}
                      required
                      rows="2"
                    />
                    <textarea
                      placeholder="Комментарий к заказу"
                      value={customerData.comment}
                      onChange={(e) => setCustomerData({...customerData, comment: e.target.value})}
                      rows="2"
                    />
                    <div className="cart-actions">
                      <button 
                        type="button"
                        onClick={() => setShowCheckoutForm(false)}
                        className="clear-cart-btn"
                      >
                        Назад
                      </button>
                      <button 
                        type="submit"
                        className="checkout-btn"
                      >
                        Подтвердить заказ
                      </button>
                      {isPaymentEnabled() && (
                        <button 
                          type="button"
                          onClick={handlePayment}
                          className="payment-btn"
                        >
                          <FaCreditCard /> Оплатить онлайн
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;